import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Ban, ShieldCheck, Download, Eye, Copy, FileText,
  ClipboardList, Database, FileBarChart, X, AlertTriangle, CheckCircle2,
  ScrollText, Archive, Link2,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ROOM_STATUS, PURPOSES } from "@/pages/DataRoom";

const ITEM_ICON = { REPORT: FileBarChart, DOCUMENT: FileText, QUESTIONNAIRE: ClipboardList,
  ESG_DATASET: Database, SUMMARY: FileText, EVIDENCE: FileText, OTHER: FileText };
const RECIPIENT_TYPES = { BANK: "Banca", CUSTOMER: "Cliente", SUPPLY_CHAIN: "Capofiliera",
  AUDITOR: "Auditor", INVESTOR: "Investitore", CONSULTANT_EXTERNAL: "Consulente esterno",
  OTHER: "Altro" };
const RECIPIENT_STATUS = { INVITED: "Invitato", ACTIVE: "Attivo", EXPIRED: "Scaduto",
  REVOKED: "Revocato", DECLINED: "Rifiutato" };

export default function DataRoomDetail() {
  const { roomId } = useParams();
  const { companyId, period, can } = useTenant();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [picker, setPicker] = useState(null);
  const [logs, setLogs] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showRecipient, setShowRecipient] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [newRec, setNewRec] = useState({ recipient_type: "BANK", organization_name: "",
    email: "", contact_name: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const canEdit = can("dataroom:edit");
  const canActivate = can("dataroom:activate");
  const canRevoke = can("dataroom:revoke");
  const canAudit = can("dataroom:audit");

  const base = `/companies/${companyId}/periods/${period?.id}/data-rooms/${roomId}`;

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(base);
      setDetail(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, period?.id, roomId]);

  useEffect(() => { load(); }, [load]);

  const addRecipient = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/recipients`, newRec);
      setInviteResult(data);
      toast.success("Invito creato: copia il link e invialo tu al destinatario");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revokeRecipient = async (rid) => {
    setBusy(true);
    try {
      await api.post(`${base}/recipients/${rid}/action`, { action: "REVOKE" });
      toast.success("Accesso del destinatario revocato con effetto immediato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async () => {
    setShowPicker(true);
    if (!picker) {
      try {
        const { data } = await api.get(
          `/companies/${companyId}/periods/${period.id}/data-rooms/content-picker`);
        setPicker(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
  };

  const addItem = async (payload) => {
    setBusy(true);
    try {
      await api.post(`${base}/items`, payload);
      toast.success("Contenuto aggiunto alla Data Room");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (itemId) => {
    setBusy(true);
    try {
      await api.delete(`${base}/items/${itemId}`);
      toast.success("Contenuto rimosso (l'accesso cessa subito)");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runPrivacyCheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/privacy-check`);
      setPrivacy(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/activate`);
      toast.success(`Data Room attiva fino al ${new Date(data.expires_at).toLocaleDateString("it-IT")}`);
      setPrivacy(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" && d?.message ? d.message : formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`${base}/revoke`);
      toast.success("Data Room revocata: token e sessioni invalidati immediatamente");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const loadLogs = async () => {
    setShowLogs(!showLogs);
    if (!showLogs && !logs) {
      try {
        const { data } = await api.get(`${base}/access-log`);
        setLogs(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
  };

  const downloadManifest = async () => {
    try {
      const { data } = await api.get(`${base}/manifest`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `manifest-dataroom-${roomId.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Manifest di condivisione scaricato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) {
    return (
      <div className="max-w-lg mx-auto mt-16" data-testid="dataroom-detail-error">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center text-sm text-red-700">{error}</CardContent>
        </Card>
      </div>
    );
  }
  if (!detail) return <p className="text-sm text-slate-500">Caricamento…</p>;

  const { room, items, recipients, versions } = detail;
  const st = ROOM_STATUS[room.status] || ROOM_STATUS.DRAFT;
  const editable = ["DRAFT", "UNDER_REVIEW", "ACTIVE"].includes(room.status);

  return (
    <div className="space-y-6" data-testid="dataroom-detail-page">
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="ghost" size="sm" className="h-8"
          onClick={() => navigate("/data-room-interna")} data-testid="dataroom-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />Data Room
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
            {room.name}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {PURPOSES[room.purpose] || room.purpose} · v{room.content_version || 1}
            {room.expires_at && ` · Scade il ${new Date(room.expires_at).toLocaleDateString("it-IT")}`}
            {" · "}{room.access_mode === "MAGIC_LINK_OTP" ? "Link + OTP" : "Link sicuro"}
          </p>
        </div>
        <Badge className={st.cls} data-testid="dataroom-detail-status">{st.label}</Badge>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          {canEdit && editable && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={runPrivacyCheck}
              disabled={busy} data-testid="privacy-check-btn">
              <ShieldCheck className="w-3 h-3 mr-1" aria-hidden="true" />Verifica privacy
            </Button>
          )}
          {canActivate && ["DRAFT", "UNDER_REVIEW"].includes(room.status) && (
            <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
              disabled={busy || !items.length || !recipients.length
                || (privacy && !privacy.can_activate)}
              onClick={activate} data-testid="activate-dataroom-btn"
              title={!items.length ? "Aggiungi contenuti" : !recipients.length
                ? "Aggiungi destinatari" : "Attiva e condividi"}>
              <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Attiva e condividi
            </Button>
          )}
          {canRevoke && room.status === "ACTIVE" && (
            <Button size="sm" variant="outline"
              className="h-8 text-xs text-red-700 border-red-300" disabled={busy}
              onClick={revoke} data-testid="revoke-dataroom-btn">
              <Ban className="w-3 h-3 mr-1" aria-hidden="true" />Revoca accesso
            </Button>
          )}
          {canAudit && (
            <>
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={loadLogs} data-testid="access-log-btn">
                <ScrollText className="w-3 h-3 mr-1" aria-hidden="true" />
                {showLogs ? "Nascondi accessi" : "Chi ha visto cosa"}
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={downloadManifest} data-testid="manifest-btn">
                <Download className="w-3 h-3 mr-1" aria-hidden="true" />Manifest condivisione
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {privacy && (
        <Card className={`border ${privacy.can_activate ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}`}
          data-testid="privacy-result">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-semibold text-slate-800">
              {privacy.can_activate
                ? "Privacy check superato" : "Privacy check fallito: rimuovi i contenuti bloccati"}
            </p>
            {(privacy.blocked || []).map((b, i) => (
              <p key={i} className="text-xs text-red-800" data-testid={`privacy-blocked-${i}`}>
                <Ban className="w-3 h-3 inline mr-1" aria-hidden="true" />{b.item}: {b.message}
              </p>
            ))}
            {(privacy.warnings || []).map((w, i) => (
              <p key={i} className="text-xs text-amber-800" data-testid={`privacy-warning-${i}`}>
                <AlertTriangle className="w-3 h-3 inline mr-1" aria-hidden="true" />
                {w.item}: {w.message}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Contenuti condivisi</CardTitle>
            {canEdit && editable && (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={openPicker} data-testid="add-item-btn">
                <Plus className="w-3 h-3 mr-1" aria-hidden="true" />Aggiungi
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2" data-testid="dataroom-items">
            {items.length === 0 && (
              <p className="text-xs text-slate-500">Nessun contenuto. Solo contenuti approvati
                e verificati sono selezionabili di default.</p>
            )}
            {items.map((it) => {
              const Icon = ITEM_ICON[it.item_type] || FileText;
              return (
                <div key={it.id}
                  className="flex items-center gap-2 rounded-md border border-slate-100 p-2.5"
                  data-testid={`dataroom-item-${it.id}`}>
                  <Icon className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{it.label}</p>
                    <p className="text-[10px] text-slate-500">
                      {it.permission === "DOWNLOAD" ? "Visualizza + scarica" : "Solo visualizzazione"}
                      {it.visibility === "SPECIFIC_RECIPIENTS" && " · destinatari selezionati"}
                    </p>
                  </div>
                  {canEdit && editable && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400"
                      onClick={() => removeItem(it.id)} disabled={busy}
                      data-testid={`remove-item-${it.id}`} aria-label="Rimuovi contenuto">
                      <X className="w-3 h-3" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Destinatari</CardTitle>
            {canEdit && editable && (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => { setShowRecipient(true); setInviteResult(null); }}
                data-testid="add-recipient-btn">
                <Plus className="w-3 h-3 mr-1" aria-hidden="true" />Invita
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2" data-testid="dataroom-recipients">
            {recipients.length === 0 && (
              <p className="text-xs text-slate-500">Nessun destinatario. Ogni invito genera
                un link sicuro monouso da inviare tu (nessuna email automatica).</p>
            )}
            {recipients.map((r) => (
              <div key={r.id}
                className="flex items-center gap-2 rounded-md border border-slate-100 p-2.5"
                data-testid={`recipient-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800">
                    {r.organization_name}
                    <span className="font-normal text-slate-500"> · {RECIPIENT_TYPES[r.recipient_type]}</span>
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {RECIPIENT_STATUS[r.status]}
                    {r.first_access_at && ` · primo accesso ${new Date(r.first_access_at).toLocaleDateString("it-IT")}`}
                    {r.last_access_at && ` · ultimo ${new Date(r.last_access_at).toLocaleString("it-IT")}`}
                  </p>
                </div>
                {canRevoke && ["INVITED", "ACTIVE"].includes(r.status) && (
                  <Button size="sm" variant="ghost"
                    className="h-6 text-[10px] text-red-600" disabled={busy}
                    onClick={() => revokeRecipient(r.id)}
                    data-testid={`revoke-recipient-${r.id}`}>Revoca</Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {versions.length > 0 && (
        <Card className="border-slate-200" data-testid="dataroom-versions">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Storico versioni contenuto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {versions.map((v) => (
              <p key={v.id} className="text-xs text-slate-600" data-testid={`version-${v.version}`}>
                <strong>v{v.version}</strong> · {v.change} ·{" "}
                {new Date(v.created_at).toLocaleString("it-IT")}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {showLogs && logs && (
        <Card className="border-slate-200" data-testid="access-log-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Registro accessi ({logs.total})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 pr-3 font-semibold">Quando</th>
                    <th className="py-1.5 pr-3 font-semibold">Destinatario</th>
                    <th className="py-1.5 pr-3 font-semibold">Azione</th>
                    <th className="py-1.5 font-semibold">Esito</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.logs.map((l) => (
                    <tr key={l.id} className="border-b border-slate-50"
                      data-testid={`log-${l.action}`}>
                      <td className="py-1.5 pr-3">{new Date(l.timestamp).toLocaleString("it-IT")}</td>
                      <td className="py-1.5 pr-3">{l.recipient_name || "—"}</td>
                      <td className="py-1.5 pr-3">{l.action}</td>
                      <td className="py-1.5">{l.success ? "OK" : "Negato"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {showRecipient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog" aria-modal="true" data-testid="recipient-dialog">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <CardTitle className="text-base">Invita destinatario</CardTitle>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => setShowRecipient(false)} data-testid="recipient-close">
                  <X className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!inviteResult ? (
                <>
                  <Select value={newRec.recipient_type}
                    onValueChange={(v) => setNewRec({ ...newRec, recipient_type: v })}>
                    <SelectTrigger data-testid="recipient-type" aria-label="Tipo destinatario">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(RECIPIENT_TYPES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="text" value={newRec.organization_name} maxLength={200}
                    onChange={(e) => setNewRec({ ...newRec, organization_name: e.target.value })}
                    placeholder="Organizzazione (es. Banca Demo — Ufficio Fidi)"
                    className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
                    data-testid="recipient-org" aria-label="Organizzazione" />
                  <input type="email" value={newRec.email} maxLength={200}
                    onChange={(e) => setNewRec({ ...newRec, email: e.target.value })}
                    placeholder="Email (facoltativa, solo per tracciabilità)"
                    className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
                    data-testid="recipient-email" aria-label="Email" />
                  <Button size="sm" className="w-full h-8 text-xs bg-green-700 text-white"
                    disabled={!newRec.organization_name.trim() || busy}
                    onClick={addRecipient} data-testid="recipient-create">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                      : "Genera invito sicuro"}
                  </Button>
                </>
              ) : (
                <div className="space-y-3" data-testid="invite-result">
                  <p className="text-xs text-slate-600">
                    Invito creato. Nessuna email automatica: copia il link e invialo tu al
                    destinatario. Il link è monouso e ad alta entropia.
                  </p>
                  <div className="flex items-center gap-2 rounded-md bg-slate-50 border border-slate-200 p-2">
                    <code className="text-[10px] text-slate-700 break-all flex-1"
                      data-testid="magic-link">{window.location.origin}{inviteResult.magic_link.startsWith("/") ? "" : "/"}{inviteResult.magic_link.replace(/^https?:\/\/[^/]+/, "")}</code>
                    <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}${inviteResult.magic_link.replace(/^https?:\/\/[^/]+/, "")}`);
                        toast.success("Link copiato");
                      }} data-testid="copy-link-btn">
                      <Copy className="w-3 h-3" aria-hidden="true" />
                    </Button>
                  </div>
                  {inviteResult.otp && (
                    <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2"
                      data-testid="otp-code">
                      Codice di verifica (comunicalo al destinatario): {inviteResult.otp}
                    </p>
                  )}
                  <Button size="sm" variant="outline" className="w-full h-8 text-xs"
                    onClick={() => { setShowRecipient(false); setInviteResult(null); }}
                    data-testid="invite-done">Fatto</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog" aria-modal="true" data-testid="content-picker-dialog">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <CardTitle className="text-base">Seleziona contenuti (solo approvati/verificati)</CardTitle>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => setShowPicker(false)} data-testid="picker-close">
                  <X className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!picker ? <p className="text-sm text-slate-500">Caricamento…</p> : (
                <>
                  {picker.reports.length > 0 && (
                    <PickerSection title="Report approvati" testid="picker-reports"
                      entries={picker.reports} onAdd={(e, perm) =>
                        addItem({ item_type: "REPORT", ref_id: e.ref_id, permission: perm })} />
                  )}
                  {picker.questionnaires.length > 0 && (
                    <PickerSection title="Questionari approvati" testid="picker-questionnaires"
                      entries={picker.questionnaires} onAdd={(e, perm) =>
                        addItem({ item_type: "QUESTIONNAIRE", ref_id: e.ref_id, permission: perm })} />
                  )}
                  {picker.documents.length > 0 && (
                    <PickerSection title="Documenti" testid="picker-documents"
                      entries={picker.documents} onAdd={(e, perm) =>
                        addItem({ item_type: "DOCUMENT", ref_id: e.ref_id, permission: perm })} />
                  )}
                  {picker.score_available && (
                    <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3">
                      <p className="text-xs text-amber-900 mb-2">
                        ESG PMI Score — metodologia proprietaria ESG-PMI (non un rating ufficiale).
                        Non incluso di default: richiede scelta esplicita.
                      </p>
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        disabled={busy}
                        onClick={() => addItem({ item_type: "SUMMARY", kind: "SCORE",
                          permission: "VIEW" })}
                        data-testid="picker-add-score">Condividi ESG PMI Score (solo vista)</Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function PickerSection({ title, entries, onAdd, testid }) {
  const [perm, setPerm] = useState("VIEW");
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-bold text-slate-700">{title}</p>
        <Select value={perm} onValueChange={setPerm}>
          <SelectTrigger className="h-7 w-44 text-xs" aria-label="Permesso">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="VIEW">Solo visualizzazione</SelectItem>
            <SelectItem value="DOWNLOAD">Visualizza + scarica</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        {entries.slice(0, 15).map((e) => (
          <div key={e.ref_id} className="flex items-center gap-2 text-xs">
            <span className="flex-1 truncate text-slate-700">{e.label}
              {e.confidentiality && e.confidentiality !== "STANDARD" && (
                <span className="text-amber-700"> ({e.confidentiality})</span>)}
            </span>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] text-green-700"
              onClick={() => onAdd(e, perm)}
              data-testid={`picker-add-${e.ref_id}`}>+ Aggiungi</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
